<template>
  <div class="c-padding flex flex-col gap-2">
    <div v-if="isAdvancedMode" class="flex flex-col gap-2">
      <div class="flex gap-1">
        <div class="basis-1/2">
          <span>{{ t('canvasUI.canvasBuilder.canvasDrawerTheme.padding.top') }}</span>
          <NInputNumber
            v-model:value="values.top"
            :format="(value) => `${value}px`"
            :min="0"
            :on-update:value="(value) => setPadding(value, 'top')"
            :parse="(formattedValue) => parseInt(formattedValue)"
            :step="4"
          />
        </div>
        <div class="basis-1/2">
          <span>{{ t('canvasUI.canvasBuilder.canvasDrawerTheme.padding.left') }}</span>
          <NInputNumber
            v-model:value="values.left"
            :format="(value) => `${value}px`"
            :min="0"
            :on-update:value="(value) => setPadding(value, 'left')"
            :parse="(formattedValue) => parseInt(formattedValue)"
            :step="4"
          />
        </div>
      </div>
      <div class="flex gap-1">
        <div class="basis-1/2">
          <span>{{ t('canvasUI.canvasBuilder.canvasDrawerTheme.padding.bottom') }}</span>
          <NInputNumber
            v-model:value="values.bottom"
            :format="(value) => `${value}px`"
            :min="0"
            :on-update:value="(value) => setPadding(value, 'bottom')"
            :parse="(formattedValue) => parseInt(formattedValue)"
            :step="4"
          />
        </div>
        <div class="basis-1/2">
          <span>{{ t('canvasUI.canvasBuilder.canvasDrawerTheme.padding.right') }}</span>
          <NInputNumber
            v-model:value="values.right"
            :format="(value) => `${value}px`"
            :min="0"
            :on-update:value="(value) => setPadding(value, 'right')"
            :parse="(formattedValue) => parseInt(formattedValue)"
            :step="4"
          />
        </div>
      </div>
    </div>
    <div v-else>
      <div class="basis-1/2">
        <NInputNumber
          v-model:value="values.top"
          :format="(value) => `${value}px`"
          :min="0"
          :on-update:value="(value) => setPadding(value)"
          :parse="(formattedValue) => parseInt(formattedValue)"
          :step="4"
        />
      </div>
    </div>
    <div class="text-size-xs">
      {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.padding.switchTo') }}
      <span class="cursor-pointer underline color-primary" @click="switchMode">
        {{
          isAdvancedMode
            ? t('canvasUI.canvasBuilder.canvasDrawerTheme.padding.simpleMode')
            : t('canvasUI.canvasBuilder.canvasDrawerTheme.padding.advancedMode')
        }}
      </span>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { reactive, ref } from 'vue'
import { NInputNumber } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import type { ThemePadding } from '@canvas-builder/composables/useCanvasTheme'

const emit = defineEmits(['update:value'])

const props = withDefaults(
  defineProps<{
    isAdvancedMode?: boolean
    values?: ThemePadding
  }>(),
  {
    isAdvancedMode: false,
    values: (props) => ({
      top: props.values?.top ?? 0,
      right: props.values?.right ?? 0,
      bottom: props.values?.bottom ?? 0,
      left: props.values?.left ?? 0,
    }),
  },
)

const { t } = useI18n()

const isAdvancedMode = ref(props.isAdvancedMode)
const values = reactive({
  top: props.values.top,
  right: props.values.right,
  bottom: props.values.bottom,
  left: props.values.left,
})

function setPadding(value: number | null, position?: 'bottom' | 'left' | 'top' | 'right') {
  if (value === null) return

  if (!isAdvancedMode.value) {
    values.top = value
    values.right = value
    values.bottom = value
    values.left = value
  } else if (isAdvancedMode.value && position) {
    values[position] = value
  }

  emit('update:value', values)
}

function switchMode() {
  isAdvancedMode.value = !isAdvancedMode.value

  values.bottom = values.top
  values.left = values.top
  values.right = values.top

  emit('update:value', values)
}
</script>
